/**
 * L'ACCUEIL — LES VENTES DU GUICHET FAITES PENDANT UNE AUTRE NUIT.
 *
 * La caisse Shotgun n°2 de l'Accueil n'avait pas été clôturée : pendant la
 * nuit du 03/10/2026, 110 billets (1 060 €) ont été vendus en restant
 * rattachés à la soirée du 26/09. L'Accueil du 03/10 affichait +1 059 € sans
 * dire pourquoi ; la DAF l'a retrouvé à la main.
 *
 * Ce panneau se pose à l'étape de l'Accueil, juste sous les cases du haut :
 * la phrase dit ce qui s'est passé, les chiffres disent combien, l'EFFET dit
 * ce que le geste changera à l'écart que l'on a sous les yeux (« attendu
 * 4 460 €, écart −1 € au lieu de +1 059 € »), et le geste se fait ici, d'un
 * clic : rattacher à la nuit de vente, ou maintenir sur la soirée (motivé).
 * Une décision prise s'annule (motivé) : le journal garde tout.
 *
 * Décider n'est pas écrire dans la clôture : c'est un événement à part
 * (`POST /finance/nights/:date/guichet-hors-nuit`, droit de traiter les écarts,
 * ré-authentification). La clôture le relit ; une clôture finalisée reste
 * intacte et le panneau dit si une révision est conseillée.
 */

import { useState } from 'react'
import { ArrowLeftRight, CheckCircle2, Undo2, X } from 'lucide-react'
import { Link } from '../router.js'
import { Modal, Select, adminMutate, useToast } from '../admin/primitives.js'
import { formatEcart, formatMoney } from './money.js'
import { MotifField, heureParis, ilYA, motifSuffisant, type ClosingApi } from './ClosingCommon.js'
import type { OffNightSaleRow } from './nights-common.js'
import type { ChiffresPointDeVente } from './closing-rail-model.js'
import { effetHorsNuit, etatDuPanneau, jourMois } from './guichet-hors-nuit-model.js'
import './finance-guichet.css'

const PILULE: Record<OffNightSaleRow['status'], string> = {
  EN_ATTENTE: 'lc-pill lc-pill--warning',
  RATTACHE: 'lc-pill lc-pill--ok',
  MAINTENU: 'lc-pill',
  SANS_OBJET: 'lc-pill lc-pill--idle',
}



export function GuichetHorsNuit(props: {
  api: ClosingApi
  /** Les chiffres de l'Accueil à l'écran (cases du haut) : l'effet du geste se dit avec eux. */
  chiffres: ChiffresPointDeVente | null
  /** La clôture est finalisée : décider reste possible, mais ne la change pas. */
  figee: boolean
}) {
  const { api, chiffres, figee } = props
  const d = api.closing
  const toast = useToast()
  const cases = d.off_night?.cases ?? []
  const peutDecider = api.peut('finance.discrepancy.manage')
  const [occupe, setOccupe] = useState<string | null>(null)
  const [cibles, setCibles] = useState<Record<string, string>>({})
  const [motif, setMotif] = useState<{ cas: OffNightSaleRow; decision: 'MAINTENIR' | 'ANNULER'; texte: string } | null>(null)

  if (cases.length === 0) return null

  const decider = async (c: OffNightSaleRow, decision: 'RATTACHER' | 'MAINTENIR' | 'ANNULER', raison: string | null) => {
    setOccupe(c.key)
    const corps: Record<string, unknown> = {
      from_event_id: c.from_event_id, decision,
      expected_decision_id: c.decision?.id ?? null,
    }
    if (raison !== null) corps['reason'] = raison
    const cible = cibles[c.key]
    if (decision === 'RATTACHER' && cible) corps['to_event_id'] = cible
    const r = await adminMutate<{ error?: string; message?: string; status_label?: string; revision?: Array<{ message: string }> }>(
      'POST', `/api/v1/finance/nights/${c.sale_business_date}/guichet-hors-nuit`, corps,
    )
    setOccupe(null)
    if (r.status >= 200 && r.status < 300 && r.data.error === undefined) {
      const conseil = r.data.revision?.[0]?.message
      toast('ok', `${r.data.status_label ?? 'Décision enregistrée'}.${conseil ? ` ${conseil}` : ''}`)
      setMotif(null)
      api.recharger()
      return
    }
    toast('error', r.data.message ?? 'La décision n’a pas pu être enregistrée.')
    if (r.status === 409) api.recharger()
  }

  return (
    <section className="lc-panel fgh" aria-labelledby="fgh-titre">
      <div className="lc-panel__head">
        <span className="fih-head__icon" aria-hidden="true"><ArrowLeftRight size={17} /></span>
        <h2 id="fgh-titre" className="lc-panel__title">Ventes du guichet d’une autre nuit</h2>
        <span className="lc-panel__spacer" />
        <span className={etatDuPanneau(cases).classe}>{etatDuPanneau(cases).texte}</span>
      </div>
      <div className="lc-panel__body fgh__corps">
        {/* Rattachés, mais ni TPE ni espèces relevés : aucune ligne où se poser.
            Le serveur ne les range pas d'office ; on le dit. */}
        {(d.off_night?.unplaced_cents ?? 0) > 0 ? (
          <p className="fi-provisoire">
            {formatMoney(d.off_night!.unplaced_cents)} rattachés à cet Accueil n’ont ni TPE ni espèces relevés chez
            Shotgun : ils ne sont dans aucune ligne de l’attendu. Saisissez-les au rapprochement, avec leur moyen de paiement.
          </p>
        ) : null}
        {cases.map((c) => {
          const effet = effetHorsNuit(c, chiffres)
          const plusieurs = (c.to_candidates ?? []).length > 1
          const cible = cibles[c.key] ?? ''
          const autreNuit = `/comptabilite/nuits/${c.other_business_date}`
          return (
            <article key={c.key} className={`fgh-cas fgh-cas--${c.status === 'EN_ATTENTE' ? 'attente' : c.status === 'RATTACHE' ? 'rattache' : 'maintenu'}`}>
              {/* Sans objet : la classe neutre du maintien, la pastille dit le reste. */}
              <div className="fgh-cas__tete">
                <span className={PILULE[c.status]}>{c.status_label}</span>
                <span className="lc-hint">{c.classe_label}</span>
              </div>
              <p className="fgh-cas__phrase">{c.sentence}</p>

              <dl className="fgh-chiffres">
                <div>
                  <dt>Vendus hors de leur nuit</dt>
                  <dd className="fi-money fi-money--fort">{formatMoney(c.total_cents)}</dd>
                </div>
                <div>
                  <dt>Dont TPE</dt>
                  <dd className="fi-money">{formatMoney(c.card_present_cents)}</dd>
                </div>
                <div>
                  <dt>Dont espèces</dt>
                  <dd className="fi-money">{formatMoney(c.cash_cents)}</dd>
                </div>
                <div>
                  <dt>Billets</dt>
                  <dd>
                    {c.tickets}
                    <span className="fgh-chiffres__sous">
                      {c.paid_tickets} payant{c.paid_tickets > 1 ? 's' : ''}
                      {c.free_tickets > 0 ? `, ${c.free_tickets} exonération${c.free_tickets > 1 ? 's' : ''}` : ''}
                      {c.scanned_tickets > 0 ? `, ${c.scanned_tickets} scanné${c.scanned_tickets > 1 ? 's' : ''} cette nuit-là` : ''}
                    </span>
                  </dd>
                </div>
              </dl>

              {effet ? <p className="fgh-effet">{effet}</p> : null}

              {c.decision ? (
                <p className="lc-hint">
                  Décision {c.decision.decision === 'RATTACHER' ? 'de rattacher' : 'de maintenir'} prise {ilYA(c.decision.created_at)}
                  {c.decision.created_by_name ? ` par ${c.decision.created_by_name}` : ''}, à {heureParis(c.decision.created_at)}
                  {c.decision.reason ? ` — « ${c.decision.reason} »` : ''}.
                  {c.undecided_tickets > 0 ? ` ${c.undecided_tickets} billet${c.undecided_tickets > 1 ? 's' : ''} vendu${c.undecided_tickets > 1 ? 's' : ''} depuis attend${c.undecided_tickets > 1 ? 'ent' : ''} une nouvelle décision.` : ''}
                </p>
              ) : null}

              {c.revision ? (
                <p className="fi-provisoire fgh-revision">
                  {c.revision.message}
                  {c.revision.ca_delta_cents !== null && c.revision.ca_delta_cents !== 0
                    ? ` Effet d’une révision sur le chiffre d’affaires de cette nuit : ${formatEcart(c.revision.ca_delta_cents).texte}.`
                    : ''}
                </p>
              ) : null}

              {figee ? (
                <p className="lc-hint">
                  Cette clôture est finalisée : une décision ne la change pas. Elle dira si une révision est conseillée.
                </p>
              ) : null}

              <div className="fgh-gestes">
                {!peutDecider ? (
                  <span className="lc-hint">Décider demande le droit de traiter les écarts.</span>
                ) : c.status === 'EN_ATTENTE' ? (
                  <>
                    {plusieurs ? (
                      <Select
                        value={cible}
                        options={(c.to_candidates ?? []).map((x) => ({ value: x.event_id, label: x.name }))}
                        onChange={(v) => setCibles((x) => ({ ...x, [c.key]: v }))}
                        placeholder="Soirée qui reçoit les ventes"
                        ariaLabel="Soirée de la nuit de vente qui reçoit ces ventes"
                        compact
                      />
                    ) : null}
                    <button
                      type="button" className="lc-btn lc-btn--primary"
                      disabled={occupe !== null || (plusieurs && !cible)}
                      onClick={() => void decider(c, 'RATTACHER', null)}
                    >
                      <CheckCircle2 size={16} aria-hidden="true" />
                      {occupe === c.key ? 'Enregistrement en cours' : c.side === 'VENTE' ? 'Rattacher à cette nuit' : `Rattacher à la nuit du ${jourMois(c.sale_business_date)}`}
                    </button>
                    <button
                      type="button" className="lc-btn lc-btn--ghost" disabled={occupe !== null}
                      onClick={() => setMotif({ cas: c, decision: 'MAINTENIR', texte: '' })}
                    >
                      {c.side === 'VENTE' ? `Maintenir sur la nuit du ${jourMois(c.from_business_date)}` : 'Maintenir sur cette nuit'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button" className="lc-btn lc-btn--ghost" disabled={occupe !== null}
                    onClick={() => setMotif({ cas: c, decision: 'ANNULER', texte: '' })}
                  >
                    <Undo2 size={16} aria-hidden="true" />Annuler la décision
                  </button>
                )}
                <Link className="fgh-lien" href={autreNuit}>
                  Voir la nuit du {jourMois(c.other_business_date)}
                </Link>
              </div>
            </article>
          )
        })}
      </div>

      <Modal open={motif !== null} onClose={() => setMotif(null)} ariaLabel="Motiver la décision" width={560} owning>
        {motif ? (
          <>
            <header className="lc-modal__head">
              <span className="lc-modal__icon" aria-hidden="true"><ArrowLeftRight size={22} /></span>
              <div>
                <div className="lc-modal__title">
                  {motif.decision === 'MAINTENIR' ? 'Maintenir ces ventes sur leur soirée' : 'Annuler la décision'}
                </div>
                <div className="lc-modal__pills">
                  <span className="lc-pill lc-pill--idle">{motif.cas.tickets} billets · {formatMoney(motif.cas.total_cents)}</span>
                </div>
              </div>
              <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={() => setMotif(null)} aria-label="Fermer">
                <X size={18} />
              </button>
            </header>
            <div className="lc-modal__body fiw-modal">
              <p className="lc-hint">
                {motif.decision === 'MAINTENIR'
                  ? `Ces ventes resteront sur la nuit du ${jourMois(motif.cas.from_business_date)}, alors que l’argent est dans la caisse du ${jourMois(motif.cas.sale_business_date)}. Dites pourquoi : c’est la trace qui restera.`
                  : 'Les ventes redeviendront en attente de décision, et la finalisation des deux nuits les attendra. Dites pourquoi : la décision annulée reste lisible au journal.'}
              </p>
              <MotifField
                id="fgh-motif"
                valeur={motif.texte}
                onChange={(t) => setMotif((m) => (m ? { ...m, texte: t } : m))}
                label={motif.decision === 'MAINTENIR' ? 'Pourquoi les maintenir ?' : 'Pourquoi annuler ?'}
              />
              <div className="fiw-modal__actions">
                <button className="lc-btn lc-btn--ghost" onClick={() => setMotif(null)}>Revenir</button>
                <button
                  className="lc-btn lc-btn--primary"
                  disabled={!motifSuffisant(motif.texte) || occupe !== null}
                  onClick={() => void decider(motif.cas, motif.decision, motif.texte.trim())}
                >
                  {motif.decision === 'MAINTENIR' ? 'Maintenir' : 'Annuler la décision'}
                </button>
              </div>
            </div>
          </>
        ) : null}
      </Modal>
    </section>
  )
}
